<template>
    <div class="container pt-3">
        <span class="small" @click="goBack()" role="button"><i class="fa-solid fa-chevron-left me-2"></i>{{ $t('common.back') }}</span>

        <div v-if="loading" class="text-center text-body-secondary py-5">
            {{ $t('common.loading') }}
        </div>

        <NotFound v-else-if="notFound" :item="$t('taskDetail.emailItem')" />

        <div v-else-if="email" class="card shadow mt-3">
            <div class="card-body">
                <h1 class="fs-5 fw-bolder mb-3">{{ email.subject }}</h1>

                <dl class="row small mb-3">
                    <template v-if="email.from_address">
                        <dt class="col-3 col-sm-2 text-body-secondary fw-normal">{{ $t('taskDetail.emailFrom') }}</dt>
                        <dd class="col-9 col-sm-10 mb-1">{{ email.from_address }}</dd>
                    </template>
                    <dt class="col-3 col-sm-2 text-body-secondary fw-normal">{{ $t('taskDetail.emailTo') }}</dt>
                    <dd class="col-9 col-sm-10 mb-1">
                        <span v-for="addr in toRecipients" :key="addr" class="badge bg-body-tertiary text-body-secondary border me-1 mb-1">{{ addr }}</span>
                    </dd>
                    <template v-if="ccRecipients.length > 0">
                        <dt class="col-3 col-sm-2 text-body-secondary fw-normal">{{ $t('taskDetail.emailCc') }}</dt>
                        <dd class="col-9 col-sm-10 mb-1">
                            <span v-for="addr in ccRecipients" :key="addr" class="badge bg-body-tertiary text-body-secondary border me-1 mb-1">{{ addr }}</span>
                        </dd>
                    </template>
                    <dt class="col-3 col-sm-2 text-body-secondary fw-normal">{{ $t('taskDetail.emailSentOn') }}</dt>
                    <dd class="col-9 col-sm-10 mb-1">{{ formatTimestamp(email.sent_on) }}</dd>
                </dl>

                <hr>

                <!-- eslint-disable-next-line vue/no-v-html -- body is sanitized with DOMPurify -->
                <div class="email-body" v-html="sanitizedBody"></div>

                <div v-if="email.attachments && email.attachments.length > 0" class="mt-4">
                    <label class="form-label"><small>{{ $t('taskDetail.attachments') }}</small></label>
                    <ul class="list-unstyled mb-0">
                        <li v-for="att in email.attachments" :key="att.id" class="my-1">
                            <a v-if="att.url" :href="att.url" target="_blank" rel="noopener"
                                class="btn btn-light w-100 text-start text-truncate small">
                                <i class="fa-solid fa-at me-2"></i>{{ att.url }}
                            </a>
                            <button v-else class="btn btn-light w-100 text-start text-truncate small"
                                @click="openAttachment(att)" :disabled="openingAttachmentId === att.id">
                                <i class="fa-solid fa-paperclip me-2"></i>{{ att.filename }}
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DOMPurify from 'dompurify';
import axios from '@/services/axios';
import { useRoute } from 'vue-router';
import { useFormatTimestamp } from '@/composables/useFormatTimestamp';
import NotFound from '@/components/NotFound.vue';

export default {
    components: { NotFound },
    setup() {
        const route = useRoute();
        const { formatTimestamp } = useFormatTimestamp();
        return {
            taskId: route.params.taskId,
            emailId: route.params.emailId,
            formatTimestamp,
        };
    },
    data() {
        return {
            email: null,
            loading: true,
            notFound: false,
            openingAttachmentId: null,
        };
    },
    computed: {
        sanitizedBody() {
            return DOMPurify.sanitize(this.email?.body || '');
        },
        toRecipients() {
            return this.splitAddresses(this.email?.to_address);
        },
        ccRecipients() {
            return this.splitAddresses(this.email?.cc_address);
        },
    },
    mounted() {
        this.fetchEmail();
    },
    methods: {
        splitAddresses(value) {
            if (!value) return [];
            return value.split(',').map(a => a.trim()).filter(Boolean);
        },
        async fetchEmail() {
            this.loading = true;
            try {
                const response = await axios.get(`tasks/${this.taskId}/emails/${this.emailId}`);
                this.email = response.data.data;
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    this.notFound = true;
                } else {
                    console.error('Error fetching email:', error);
                    this.$root.$refs.globalAlert.showAlert(this.$t('common.failed'), 'danger');
                }
            } finally {
                this.loading = false;
            }
        },
        async openAttachment(att) {
            if (this.openingAttachmentId) return;
            this.openingAttachmentId = att.id;
            // Open the tab synchronously (still within the click gesture) so popup
            // blockers don't block it; we navigate it once the authed blob loads.
            const newTab = window.open('', '_blank');
            try {
                const response = await axios({
                    url: `tasks/${this.taskId}/emails/${this.emailId}/files/${att.id}`,
                    method: 'GET',
                    responseType: 'blob',
                });
                const fileURL = window.URL.createObjectURL(response.data);
                if (newTab) {
                    newTab.location = fileURL;
                } else {
                    // Popup was blocked despite the sync open — fall back.
                    window.open(fileURL, '_blank');
                }
                // Give the new tab time to load the blob before revoking.
                setTimeout(() => window.URL.revokeObjectURL(fileURL), 60000);
            } catch (error) {
                if (newTab) newTab.close();
                console.error('Error opening attachment:', error);
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.attachmentFailed'), 'danger');
            } finally {
                this.openingAttachmentId = null;
            }
        },

        goBack() {
            if (this.$route.meta.internalNavigation && window.history.length > 2) {
                this.$router.go(-1);
            } else {
                this.$router.push('/');
            }
        },
    },
};
</script>

<style scoped>
.email-body {
    overflow-wrap: break-word;
}
.email-body :deep(img) {
    max-width: 100%;
    height: auto;
}
</style>
